import React, { useEffect, useState } from "react";
import { Container, Card, Form, Button, Row, Col } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import full_logo from "assets/images/full_logo_green.png";
import UserService from "services/UserService";
import {
  PLEASE_ENTER_VALID_EMAIL,
  PLEASE_ENTER_PASSWORD,
  LABEL_ENTER_USERNAME,
  LABEL_ENTER_PASSWORD,
  CONFIRMATION_CODE_RESEND_FAILED,
  CONFIRMATION_CODE_RESENT,
  LOGIN_FAILED,
  PLEASE_TRY_AGAIN_LATER,
  SESSION_EXPIRED,
} from "configurations/MessageConstants";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Spinner } from "react-bootstrap";
import { setUser } from "reduxStore/slices/globalSlice";
import { AuthHelper } from "utils/AuthHelper";
import EmailPopup from "./forgotPassword/EmailPopup";
import ResetPasswordPopup from "./forgotPassword/ResetPasswordPopup";
import ConfirmationCodePopup from "./registration/ConfirmationCodePopup";
import "styles/Login.css";
import "styles/PasswordPopup.css";
import {
  APP_VERSION,
  LOCAL_STORAGE_KEYS,
  USER_NOT_CONFIRMED_CODE,
} from "configurations/Constants";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showResetPasswordPopup, setShowResetPasswordPopup] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const defaultValues = {
    email: "",
    password: "",
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_EXPIRED)) {
      toast.error(SESSION_EXPIRED);
      setTimeout(() => {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_EXPIRED);
      }, 1000);
    }
  }, []);

  const handleLogin = async (data) => {
    try {
      const user = await UserService.userLogin(data.email, data.password);
      dispatch(
        setUser({
          email: user.user.email,
          firstName: user.user.firstName,
          lastName: user.user.lastName,
        })
      );
      AuthHelper.setToken(
        user.user.email,
        user.idToken,
        user.accessToken,
        user.refreshToken
      );
      navigate("/sites", { replace: true });
    } catch (error) {
      console.error(LOGIN_FAILED, error);
      const errorResponse = error.response.data.detail;
      if (errorResponse.error_code === USER_NOT_CONFIRMED_CODE) {
        try {
          await UserService.resendConfirmationCode(data.email);
          toast.success(CONFIRMATION_CODE_RESENT);
          setShowConfirmationPopup(true);
        } catch (error) {
          console.error(CONFIRMATION_CODE_RESEND_FAILED, error);
          toast.error(CONFIRMATION_CODE_RESEND_FAILED);
        }
      } else if (errorResponse.error_code) {
        toast.error(errorResponse.error);
      } else {
        toast.error(
          <div>
            {LOGIN_FAILED}
            <br />
            {PLEASE_TRY_AGAIN_LATER}
          </div>
        );
      }
    }
  };

  const handleForgotPasswordClick = () => {
    setShowEmailPopup(true);
  };

  const handleShowResetPasswordPopup = () => {
    setShowResetPasswordPopup(true);
    setShowEmailPopup(false);
  };

  const handleEmailPopupClose = () => {
    setShowEmailPopup(false);
    setResetEmail("");
  };

  const handleResetPasswordClose = () => {
    setShowResetPasswordPopup(false);
    setShowEmailPopup(false);
    setResetEmail("");
  };

  const handleConfirmationPopupClose = () => {
    setShowConfirmationPopup(false);
  };

  return (
    <>
      <EmailPopup
        show={showEmailPopup}
        resetEmail={resetEmail}
        onClose={handleEmailPopupClose}
        onShowResetPasswordPopup={handleShowResetPasswordPopup}
        setResetEmail={setResetEmail}
      />
      <ResetPasswordPopup
        show={showResetPasswordPopup}
        onResetPasswordClose={handleResetPasswordClose}
        resetEmail={resetEmail}
      />
      <ConfirmationCodePopup
        show={showConfirmationPopup}
        onClose={handleConfirmationPopupClose}
        email={watch("email")}
      />
      <Container
        fluid
        className="d-flex justify-content-center align-items-center vh-100 login-container"
      >
        <Row className="w-100 justify-content-center">
          <Col xs={12} sm={8} md={6} lg={4} className="mx-auto">
            <Card className="login-card">
              <div className="d-flex justify-content-center">
                <img src={full_logo} alt="GFA Logo" height="120" width="auto" />
              </div>
              <Card.Body>
                <Form noValidate onSubmit={handleSubmit(handleLogin)}>
                  <Form.Group className="mb-3" controlId="formBasicEmail">
                    <Form.Label className="">Username</Form.Label>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: PLEASE_ENTER_VALID_EMAIL,
                        validate: (value) =>
                          AuthHelper.isValidEmail(value) ||
                          PLEASE_ENTER_VALID_EMAIL,
                      }}
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          type="email"
                          autoComplete="email"
                          placeholder={LABEL_ENTER_USERNAME}
                          className="input-underline"
                          isInvalid={!!errors.email}
                          maxLength={50}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formBasicPassword">
                    <Form.Label className="">Password</Form.Label>
                    <div className="position-relative">
                      <Controller
                        name="password"
                        control={control}
                        rules={{ required: PLEASE_ENTER_PASSWORD }}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder={LABEL_ENTER_PASSWORD}
                            className="input-underline"
                            isInvalid={!!errors.password}
                            maxLength={50}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password?.message}
                      </Form.Control.Feedback>
                      <Button
                        variant="link"
                        className="password-toggle-button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        <i
                          className={`fa fa-eye${showPassword ? "" : "-slash"}`}
                          aria-hidden="true"
                        ></i>
                      </Button>
                    </div>
                  </Form.Group>
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="link"
                      className="p-1"
                      onClick={handleForgotPasswordClick}
                    >
                      <small>Forgot Password?</small>
                    </Button>
                  </div>
                  <Button
                    type="submit"
                    className="w-100 py-2 mt-3 fs-5"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Login"
                    )}
                  </Button>
                </Form>
                <div className="text-center mt-4">
                  <small className="text-muted">
                    Don't have an account?{" "}
                    <Link to="/registration" className="login-link-button">
                      Register Now
                    </Link>
                  </small>
                </div>
              </Card.Body>
              <small className="version-text">Version {APP_VERSION}</small>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Login;
